import React, { useState, useEffect } from 'react';
import './GitHubReadme.css';

const GitHubReadme = ({ repoUrl, className = '' }) => {
  const [readmeContent, setReadmeContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReadme = async () => {
      try {
        setLoading(true);
        setError(null);

        // Extract owner and repo from GitHub URL
        const match = repoUrl.match(/github\.com\/([^/]+)\/([^/]+)/);
        if (!match) {
          throw new Error('Invalid GitHub repository URL');
        }

        const [, owner, repo] = match;
        
        // Try multiple README file names
        const readmeNames = ['README.md', 'readme.md', 'Readme.md', 'README.MD'];
        let content = null;
        
        for (const readmeName of readmeNames) {
          try {
            // Fetch raw README content from GitHub
            const response = await fetch(
              `https://raw.githubusercontent.com/${owner}/${repo}/main/${readmeName}`
            );
            
            if (!response.ok) {
              // Try master branch if main doesn't exist
              const masterResponse = await fetch(
                `https://raw.githubusercontent.com/${owner}/${repo}/master/${readmeName}`
              );
              if (masterResponse.ok) {
                content = await masterResponse.text();
                break;
              }
            } else {
              content = await response.text();
              break;
            }
          } catch (err) {
            // Continue trying other README names
            continue;
          }
        }

        if (!content) {
          throw new Error('README file not found');
        }

        // Process markdown content for display
        // This is a simple markdown to HTML converter
        // For production, consider using a library like marked or react-markdown
        const processedContent = processMarkdown(content, owner, repo);
        setReadmeContent(processedContent);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (repoUrl) {
      fetchReadme();
    }
  }, [repoUrl]);

  // Simple markdown processor with XSS protection
  const processMarkdown = (markdown, owner, repo) => {
    // Escape HTML to prevent XSS
    const escapeHtml = (unsafe) => {
      return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    };

    // Split into lines for processing
    const lines = markdown.split('\n');
    const processedLines = [];

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];

      // Process headers (before escaping)
      if (line.startsWith('### ')) {
        const text = escapeHtml(line.substring(4));
        processedLines.push(`<h3>${text}</h3>`);
        continue;
      }
      if (line.startsWith('## ')) {
        const text = escapeHtml(line.substring(3));
        processedLines.push(`<h2>${text}</h2>`);
        continue;
      }
      if (line.startsWith('# ')) {
        const text = escapeHtml(line.substring(2));
        processedLines.push(`<h1>${text}</h1>`);
        continue;
      }

      // Escape the line
      line = escapeHtml(line);

      // Convert bold (after escaping, so safe)
      line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      line = line.replace(/__(.+?)__/g, '<strong>$1</strong>');

      // Convert italic
      line = line.replace(/\*(.+?)\*/g, '<em>$1</em>');

      // Convert inline code
      line = line.replace(/`([^`]+)`/g, '<code>$1</code>');

      // Convert links (carefully sanitize URL)
      line = line.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
        // Only allow http/https URLs
        const sanitizedUrl = url.trim();
        if (sanitizedUrl.startsWith('http://') || sanitizedUrl.startsWith('https://')) {
          return `<a href="${sanitizedUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        }
        // For relative URLs, prepend GitHub path
        const fullUrl = `https://github.com/${owner}/${repo}/blob/main/${sanitizedUrl}`;
        return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      });

      processedLines.push(line);
    }

    return processedLines.join('<br>');
  };

  if (loading) {
    return (
      <div className={`github-readme-container ${className}`}>
        <div className="readme-loading">
          <div className="loading-spinner"></div>
          <p>Loading README...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`github-readme-container ${className}`}>
        <div className="readme-error">
          <p>⚠️ Unable to load README</p>
          <small>{error}</small>
        </div>
      </div>
    );
  }

  return (
    <div className={`github-readme-container ${className}`}>
      <div className="readme-header">
        <span className="readme-badge">📖 README</span>
        <a 
          href={repoUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="github-link"
        >
          View on GitHub →
        </a>
      </div>
      <div 
        className="readme-content"
        dangerouslySetInnerHTML={{ __html: readmeContent }}
      />
    </div>
  );
};

export default GitHubReadme;
